// src/features/user-management/auth/utils/next-path.ts

/**
 * Where a rejected or absent `next` lands: campaign home.
 */
export const CAMPAIGN_HOME = "/";

/**
 * Routes that may never be a `next` destination, because landing on one after
 * signing in sends you straight back to where you just were.
 */
const AUTH_ROUTES = ["/signin", "/join", "/auth/link"];

/**
 * A base that exists only so `URL` will parse a relative path.
 *
 * Deliberately a sentinel rather than `window.location.origin`: everything
 * accepted here is a relative path by construction, so the real origin adds
 * nothing but a dependency on the environment the check runs in. Anything that
 * resolves to a different origin than this base is, by definition, trying to
 * leave ours.
 */
const PARSE_BASE = "https://next.invalid";

/**
 * Control characters browsers strip from URLs before resolving them.
 *
 * Matching them is the entire purpose, hence the disable: a tab or newline
 * smuggled into a candidate is invisible to every other check here and gone by
 * the time the browser resolves what is left.
 */
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[ -]/;

/**
 * Whether a candidate could resolve somewhere other than this origin.
 *
 * The three shapes that do: a value that is not rooted at `/` at all, a
 * protocol-relative `//host`, and a backslash, which browsers normalise to `/`
 * so that a backslash-escaped host is protocol-relative by the time it is
 * resolved. Control characters are stripped rather than rejected by the
 * browser, so a tab smuggled between the slashes becomes a scheme after the
 * strip -- reject the lot.
 */
function escapesOrigin(value: string): boolean {
  return (
    !value.startsWith("/") ||
    value.startsWith("//") ||
    value.includes("\\") ||
    CONTROL_CHARS.test(value)
  );
}

/**
 * Validate a `next` destination before it is ever handed to a navigation call.
 *
 * An unvalidated return destination is an open redirect: an attacker who can
 * get `/signin?next=https://evil.test` in front of a member gets a sign-in
 * page on the real origin that hands the session off to theirs. So this
 * accepts exactly one thing -- a same-origin path beginning with a single
 * `/` -- and returns `null` for everything else.
 *
 * The candidate is checked twice: as written, and once decoded. An encoded
 * slash passes every structural test in its raw form and is protocol-relative
 * the moment anything decodes it. The decoded pass allows characters the raw
 * pass does not (a space, say), because `%20` in a path is ordinary and only
 * the origin-escaping shapes matter once decoded.
 *
 * @param next The raw query-parameter value, which may be absent
 * @returns The path to navigate to, including query and hash, or `null`
 */
export function safeNextPath(next: string | null | undefined): string | null {
  if (typeof next !== "string" || next.length === 0) return null;

  // As written: no whitespace of any kind, on top of the origin checks.
  if (/\s/.test(next) || escapesOrigin(next)) return null;

  let decoded: string;
  try {
    decoded = decodeURIComponent(next);
  } catch {
    // Malformed percent-encoding. Nothing legitimate produces it.
    return null;
  }
  if (escapesOrigin(decoded)) return null;

  let url: URL;
  try {
    url = new URL(next, PARSE_BASE);
  } catch {
    return null;
  }
  if (url.origin !== PARSE_BASE) return null;

  // Compared as a whole pathname, not a prefix: `/joinery` is a real route and
  // has nothing to do with `/join`.
  if (AUTH_ROUTES.includes(url.pathname)) return null;

  return `${url.pathname}${url.search}${url.hash}`;
}

/**
 * Build the sign-in URL that carries the caller's current location back out
 * again.
 *
 * The parameter is omitted entirely when the destination would be rejected on
 * the way back in, so `/signin` never carries a `next` that cannot be honoured.
 *
 * @param location The location being left, as `useLocation()` supplies it
 * @returns `/signin`, with a `next` parameter when there is one worth carrying
 */
export function signInPathFor(location: {
  pathname: string;
  search?: string;
}): string {
  const destination = `${location.pathname}${location.search ?? ""}`;
  if (safeNextPath(destination) === null) return "/signin";
  return `/signin?next=${encodeURIComponent(destination)}`;
}
